import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import {
  PRODUCT_NAMES,
  buildAboutVersionCopyText,
  buildRepositoryCommitUrl,
  formatDeployEnvironmentLabel,
  normalizeReleaseVersion,
} from '@cnapse/shared';
import { AppButton } from '../design-system';
import { healthService } from '../../services/HealthService';
import { useApp } from '../../context/AppContext';

interface AboutVersionInfo {
  version: string;
  commit?: string;
  apiCommit?: string;
  environment?: string;
  repositoryUrl?: string;
}

function readFrontendVersion(): string | undefined {
  return import.meta.env.VITE_APP_VERSION?.trim() || undefined;
}

function readFrontendCommit(): string | undefined {
  return import.meta.env.VITE_BUILD_SHA?.trim() || undefined;
}

function readDeployEnvironment(): string | undefined {
  return import.meta.env.VITE_DEPLOY_ENVIRONMENT?.trim() || undefined;
}

function readRepositoryUrl(): string | undefined {
  return import.meta.env.VITE_REPOSITORY_URL?.trim() || undefined;
}

function resolveDisplayedVersion(
  frontendVersion: string | undefined,
  apiVersion: string | undefined,
): string {
  return normalizeReleaseVersion(frontendVersion ?? apiVersion ?? '0.0.0');
}

export const AboutSection: React.FC = () => {
  const { actions } = useApp();
  const [loading, setLoading] = useState(true);
  const [versionInfo, setVersionInfo] = useState<AboutVersionInfo | null>(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadVersion = async (): Promise<void> => {
      setLoading(true);
      const frontendVersion = readFrontendVersion();
      const frontendCommit = readFrontendCommit();
      const deployEnvironment = readDeployEnvironment();
      const repositoryUrl = readRepositoryUrl();

      try {
        const data = await healthService.getVersion();
        if (!cancelled) {
          const apiCommit = data.buildSha?.trim() || undefined;
          setVersionInfo({
            version: resolveDisplayedVersion(frontendVersion, data.version),
            commit: frontendCommit ?? apiCommit,
            apiCommit: apiCommit && apiCommit !== frontendCommit ? apiCommit : undefined,
            environment: deployEnvironment ?? data.environment,
            repositoryUrl,
          });
        }
      } catch (error) {
        console.error('Failed to fetch version:', error);
        if (!cancelled) {
          if (frontendVersion || frontendCommit) {
            setVersionInfo({
              version: resolveDisplayedVersion(frontendVersion, undefined),
              commit: frontendCommit,
              environment: deployEnvironment,
              repositoryUrl,
            });
          } else {
            setVersionInfo(null);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadVersion();

    return () => {
      cancelled = true;
    };
  }, []);

  const copyText = useMemo(
    () => (
      versionInfo
        ? buildAboutVersionCopyText({
          application: PRODUCT_NAMES.INSIGHT,
          platform: PRODUCT_NAMES.CCI_FULL,
          version: versionInfo.version,
          commit: versionInfo.commit,
          apiCommit: versionInfo.apiCommit,
          environment: versionInfo.environment,
        })
        : ''
    ),
    [versionInfo],
  );

  const environmentLabel = useMemo(
    () => formatDeployEnvironmentLabel(versionInfo?.environment),
    [versionInfo?.environment],
  );

  const commitUrl = useMemo(
    () => buildRepositoryCommitUrl(versionInfo?.repositoryUrl, versionInfo?.commit),
    [versionInfo?.commit, versionInfo?.repositoryUrl],
  );

  const handleCopy = useCallback(async () => {
    if (!copyText) return;

    setCopying(true);
    try {
      await navigator.clipboard.writeText(copyText);
      actions.showSnackbar('Version info copied to clipboard', 'success');
    } catch (error) {
      console.error('Failed to copy version info:', error);
      actions.showSnackbar('Failed to copy version info', 'error');
    } finally {
      setCopying(false);
    }
  }, [actions, copyText]);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" gutterBottom>
          About
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {PRODUCT_NAMES.INSIGHT} — web portal for {PRODUCT_NAMES.CCI_FULL}.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : versionInfo ? (
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Application
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {PRODUCT_NAMES.INSIGHT}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Version
            </Typography>
            <Typography variant="body1" fontWeight={500} fontFamily="monospace">
              {versionInfo.version}
              {versionInfo.commit ? ` · ${versionInfo.commit}` : ''}
            </Typography>
          </Box>
          {versionInfo.commit ? (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Commit
              </Typography>
              {commitUrl ? (
                <Link
                  href={commitUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body1"
                  fontWeight={500}
                  fontFamily="monospace"
                  underline="hover"
                >
                  {versionInfo.commit}
                </Link>
              ) : (
                <Typography variant="body1" fontWeight={500} fontFamily="monospace">
                  {versionInfo.commit}
                </Typography>
              )}
            </Box>
          ) : null}
          {environmentLabel ? (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Environment
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {environmentLabel}
              </Typography>
            </Box>
          ) : null}
          {versionInfo.apiCommit ? (
            <Alert severity="warning" variant="outlined">
              API is running commit {versionInfo.apiCommit}, which differs from this UI build.
            </Alert>
          ) : null}
          <Box>
            <AppButton
              variant="outlined"
              size="small"
              startIcon={<CopyIcon />}
              onClick={() => void handleCopy()}
              disabled={copying}
            >
              Copy version info
            </AppButton>
          </Box>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Version information is unavailable right now.
        </Typography>
      )}
    </Stack>
  );
};
